import { createReducer, on } from '@ngrx/store';

import { AuthTokenResponse, BusinessAccount, EnabledSolutionPermissions, ErrorMessage, LinkedBusinessAccount, User } from '@shared/models';

import { AuthActions } from './auth.actions';
import { LoginPageActions } from './login-page.actions';

export const authFeatureKey = 'status';

export interface AuthState {
  user: User | null;
  businessAccount: LinkedBusinessAccount | null;
  businessAccountDetails: BusinessAccount | null;
  enabledSolutions: EnabledSolutionPermissions | null;
  isLoading: boolean;
  error: ErrorMessage;
  tokenInfo: AuthTokenResponse | null;
  tenant: string | null;
  isSponsorTenant: boolean | undefined;
  showSplashScreen: boolean;
  linkedBusinessAccounts: LinkedBusinessAccount[] | null;
  linkedBusinessAccountsLoading: boolean;
}

export const initialAuthState: AuthState = {
  user: null,
  businessAccount: null,
  businessAccountDetails: null,
  enabledSolutions: null,
  isLoading: false,
  error: null,
  tokenInfo: null,
  tenant: null,
  isSponsorTenant: undefined,
  showSplashScreen: false,
  linkedBusinessAccounts: null,
  linkedBusinessAccountsLoading: false,
};

export const reducer = createReducer(
  initialAuthState,
  on(
    AuthActions.loginSuccess,
    (state, { user, businessAccount, tenant }): AuthState => ({
      ...state,
      user,
      businessAccount,
      tenant,
    }),
  ),
  on(
    AuthActions.selectBusinessAccount,
    (state, { businessAccount }): AuthState => ({
      ...state,
      businessAccount,
    }),
  ),
  on(AuthActions.logout, (): AuthState => initialAuthState),
  on(AuthActions.refreshTokenSuccess, (state, { tokenInfo }): AuthState => ({ ...state, tokenInfo })),
  on(AuthActions.businessAccountDetails, (state, { businessAccountDetails }): AuthState => ({ ...state, businessAccountDetails })),
  on(
    AuthActions.rehydrateBusinessAccountDetails,
    (state, { businessAccountDetails }): AuthState => ({
      ...state,
      businessAccountDetails,
    }),
  ),
  on(AuthActions.setSponsorAuthTenant, (state, { isSponsorTenant }): AuthState => ({ ...state, isSponsorTenant })),
  on(AuthActions.showSplashScreen, (state): AuthState => ({ ...state, showSplashScreen: true })),
  on(LoginPageActions.splashScreenAnimationEnd, (state): AuthState => ({ ...state, showSplashScreen: false })),
  on(AuthActions.loadEnabledSolutionsSuccess, (state, { enabledSolutions }): AuthState => ({ ...state, enabledSolutions })),
  on(AuthActions.loadEnabledSolutionsFailure, (state): AuthState => ({ ...state, enabledSolutions: null })),
  on(AuthActions.linkedBusinessAccountsListLoad, (state): AuthState => ({ ...state, linkedBusinessAccountsLoading: true })),
  on(
    AuthActions.linkedBusinessAccountsListSuccess,
    (state, { linkedBusinessAccounts }): AuthState => ({ ...state, linkedBusinessAccountsLoading: false, linkedBusinessAccounts }),
  ),
  on(
    AuthActions.linkedBusinessAccountsListFailure,
    (state): AuthState => ({ ...state, linkedBusinessAccountsLoading: false, linkedBusinessAccounts: [] }),
  ),
);

export const getToken = (state: AuthState) => state?.tokenInfo;

export const getUser = (state: AuthState) => state?.user;

export const getBusinessAccount = (state: AuthState) => state?.businessAccount;

export const getBusinessAccountDetails = (state: AuthState) => state?.businessAccountDetails;

export const getTenant = (state: AuthState) => state?.tenant;

export const getSponsorTenant = (state: AuthState) => state?.isSponsorTenant;

export const getShowSplashScreen = (state: AuthState) => state?.showSplashScreen;

export const getEnabledSolutions = (state: AuthState) => state?.enabledSolutions;
export const getLinkedBusinessAccounts = (state: AuthState) => state?.linkedBusinessAccounts;

export const getLinkedBusinessAccountsLoading = (state: AuthState) => state?.linkedBusinessAccountsLoading;
